import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { ArrowLeft, Check } from 'lucide-react';
import { TURNSTILE_SITE_KEY } from '@/config/env';
import { isLocalhostHost, isProductionHost } from '@/config/hosts';
import { setSecureToken } from '@/utils/tokenEncryption';
import { useApp } from '@/store/AppContext';
import registerImage from '@/img/register.jpg';
import { useTranslation } from '@/i18n/useTranslation';

// Global type for Cloudflare Turnstile
declare global {
  interface Window {
    turnstile?: any;
  }
}

export default function SignUp() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { setIsAuthenticated, exitGuestMode, loadMe } = useApp();
  const { t } = useTranslation();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [passwordMode, setPasswordMode] = useState(false);
  const [acceptTerms, setAcceptTerms] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [isRegistered, setIsRegistered] = useState(false);
  const [step, setStep] = useState<'email' | 'code'>('email');
  const [code, setCode] = useState(['', '', '', '']);
  const [turnstileToken, setTurnstileToken] = useState<string | null>(null);
  const turnstileContainerRef = useRef<HTMLDivElement>(null);
  const codeInputRefs = useRef<Array<HTMLInputElement | null>>([]);
  const codeFormRef = useRef<HTMLFormElement>(null);

  useEffect(() => {
    const prefilledEmail = (searchParams.get('email') || '').trim();
    const adminMode = searchParams.get('admin') === '1';
    if (prefilledEmail && !email) {
      setEmail(prefilledEmail);
    }
    if (adminMode) {
      setPasswordMode(true);
    }
  }, [email, searchParams]);

  useEffect(() => {
    const hostname = window.location.hostname;
    const isProduction = isProductionHost(hostname);
    
    // Skip Turnstile on localhost and staging
    if (isLocalhostHost(hostname) || !isProduction) {
      setTurnstileToken('dummy-token-for-staging');
      return;
    }

    // Clear any existing Turnstile instances
    if (turnstileContainerRef.current) {
      turnstileContainerRef.current.innerHTML = '';
    }

    const initTurnstile = () => {
      if (window.turnstile && TURNSTILE_SITE_KEY && turnstileContainerRef.current) {
        try {
          window.turnstile.render(turnstileContainerRef.current, {
            sitekey: TURNSTILE_SITE_KEY,
            mode: 'invisible',
            callback: function(token: string) {
              setTurnstileToken(token);
            },
            'error-callback': function() {
              setTurnstileToken('turnstile-error-fallback');
            },
            'theme': 'dark',
            'refresh-expired': 'auto'
          });
        } catch (error) {
          setTurnstileToken('turnstile-render-error-fallback');
        }
      } else {
        setTimeout(() => {
          if (!turnstileToken) {
            setTurnstileToken('turnstile-unavailable-fallback');
          }
        }, 5000);
      }
    };

    if (window.turnstile) {
      initTurnstile();
    } else {
      const checkTurnstile = setInterval(() => {
        if (window.turnstile) {
          clearInterval(checkTurnstile);
          initTurnstile();
        }
      }, 500);

      setTimeout(() => {
        clearInterval(checkTurnstile);
        if (!turnstileToken) {
          setTurnstileToken('turnstile-timeout-fallback');
        }
      }, 10000);
    }

    return () => {
      if (turnstileContainerRef.current) {
        turnstileContainerRef.current.innerHTML = '';
      }
    };
  }, []);

  const loginWithPassword = async () => {
    if (!password) {
      throw new Error('Password is required for administrator accounts');
    }
    const apiUrl = window.location.origin;
    const response = await fetch(`${apiUrl}/api/auth/login`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        email,
        password,
        turnstile_token: turnstileToken
      })
    });
    const data = await response.json();
    if (!response.ok) {
      throw new Error(data.errors?.[0]?.message || 'Login failed');
    }

    const accessToken = data?.data?.access_token;
    const refreshToken = data?.data?.refresh_token;
    if (!accessToken) {
      throw new Error('No access token returned by server');
    }

    setSecureToken('auth_token', accessToken);
    if (refreshToken) setSecureToken('refresh_token', refreshToken);
    localStorage.removeItem('guest_mode');
    setIsAuthenticated(true);
    exitGuestMode();
    await loadMe();
    navigate('/home', { replace: true });
  };

  const startRegistrationFlow = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const hostname = window.location.hostname;
      const isProduction = isProductionHost(hostname);

      if (!passwordMode && !acceptTerms) {
        throw new Error('Please accept the Terms & Conditions and Privacy Policy');
      }

      // Only require Turnstile on production
      if (isProduction && !turnstileToken) {
        throw new Error('Please complete the security check');
      }

      if (passwordMode) {
        await loginWithPassword();
        return;
      }

      // Use backend proxy endpoint for Turnstile validation
      const apiUrl = window.location.origin;
      
      const response = await fetch(`${apiUrl}/api/auth/register/init`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email,
          turnstile_token: turnstileToken // Send token to backend for validation
        })
      });

      const data = await response.json();

      if (!response.ok) {
        if (response.status === 409 && data?.require_password_login) {
          setPasswordMode(true);
          setError(data.errors?.[0]?.message || 'Administrator account detected. Sign in with your password.');
          return;
        }
        throw new Error(data.errors?.[0]?.message || 'Registration failed');
      }

      setStep('code');
      setCode(['', '', '', '']);
      setTimeout(() => codeInputRefs.current[0]?.focus(), 0);
    } catch (err: any) {
      setError(err.message || 'An error occurred during registration');
    } finally {
      setLoading(false);
    }
  };

  const handleCodeChange = (idx: number, value: string) => {
    const digit = value.replace(/\D/g, '').slice(0, 1);
    const nextCode = [...code];
    nextCode[idx] = digit;
    setCode((prev) => {
      const next = [...prev];
      next[idx] = digit;
      return next;
    });
    if (digit && idx < 3) {
      codeInputRefs.current[idx + 1]?.focus();
    }
    if (digit && idx === 3 && nextCode.every((part) => part.length === 1)) {
      setTimeout(() => codeFormRef.current?.requestSubmit(), 0);
    }
  };

  const handleCodeKeyDown = (idx: number, event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Backspace' && !code[idx] && idx > 0) {
      codeInputRefs.current[idx - 1]?.focus();
    }
  };

  const verifyRegistrationCode = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      const fullCode = code.join('');
      if (fullCode.length !== 4) {
        throw new Error('Please enter 4-digit verification code');
      }
      const apiUrl = window.location.origin;

      const response = await fetch(`${apiUrl}/api/auth/register/verify`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email,
          code: fullCode
        })
      });

      const data = await response.json();
      if (!response.ok) {
        throw new Error(data.errors?.[0]?.message || data.error || 'Code verification failed');
      }

      const accessToken = data?.data?.access_token;
      const refreshToken = data?.data?.refresh_token;
      if (!accessToken) {
        throw new Error('No access token returned by server');
      }

      setSecureToken('auth_token', accessToken);
      if (refreshToken) setSecureToken('refresh_token', refreshToken);
      localStorage.removeItem('guest_mode');
      setIsAuthenticated(true);
      exitGuestMode();
      await loadMe();

      setIsRegistered(true);
      setTimeout(() => navigate('/onboarding', { replace: true }), 1000);
    } catch (err: any) {
      setError(err.message || 'Verification failed');
    } finally {
      setLoading(false);
    }
  };

  const resendCode = async () => {
    setError('');
    setLoading(true);
    try {
      const apiUrl = window.location.origin;
      const response = await fetch(`${apiUrl}/api/auth/register/init`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email,
          turnstile_token: turnstileToken
        })
      });
      const data = await response.json();
      if (!response.ok) {
        throw new Error(data.errors?.[0]?.message || 'Failed to resend code');
      }
      setCode(['', '', '', '']);
      codeInputRefs.current[0]?.focus();
    } catch (err: any) {
      setError(err.message || 'Failed to resend code');
    } finally {
      setLoading(false);
    }
  };

  if (isRegistered) {
    return (
      <div className="min-h-screen bg-bg-main flex items-center justify-center">
        <div className="text-center">
          <div className="w-20 h-20 mx-auto mb-6 rounded-full bg-green-500/20 flex items-center justify-center">
            <Check className="w-10 h-10 text-green-500" strokeWidth={2} />
          </div>
          <h2 className="text-2xl font-bold text-text-primary mb-2">{t('auth.registrationSuccess')}</h2>
          <p className="text-text-secondary">{t('auth.redirectingToSignIn')}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-bg-main flex flex-col md:flex-row">
      {/* Left - Image (hidden on mobile) */}
      <div className="hidden md:block w-1/2 relative overflow-hidden">
        <img 
          src={registerImage} 
          alt="Join Paqshot" 
          className="w-full h-screen object-cover"
        />
      </div>

      {/* Right - Form (full width on mobile) */}
      <div className="w-full md:w-1/2 flex items-center justify-center p-6 md:p-12 overflow-y-auto">
        <div className="w-full max-w-md">
          <button
            onClick={() => navigate('/')}
            className="flex items-center gap-2 text-text-secondary hover:text-text-primary mb-8 transition-colors"
          >
            <ArrowLeft size={16} />
            <span className="text-sm">{t('auth.backToHome')}</span>
          </button>

          <h1 className="text-3xl font-bold text-text-primary mb-2">
            {step === 'email' ? t('auth.signUp') : t('auth.enterVerificationCode')}
          </h1>
          <p className="text-text-secondary mb-8">
            {step === 'email'
              ? t('welcome.createAccountDesc')
              : t('auth.codeSentTo', { email })}
          </p>

          {error && (
            <div className="mb-6 p-4 bg-red-500/10 border border-red-500/20 rounded-lg text-red-500 text-sm">
              {error}
            </div>
          )}

          {step === 'email' ? (
            <form onSubmit={startRegistrationFlow} className="space-y-5">
              <div>
                <label className="block text-sm font-medium text-text-primary mb-2">{t('auth.email')}</label>
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="w-full h-11 px-4 bg-bg-control border border-border rounded-lg text-text-primary placeholder-text-secondary outline-none transition-all focus:border-accent focus:bg-bg-control-hover"
                  placeholder="your@email.com"
                  required
                />
              </div>

              {passwordMode && (
                <div>
                  <label className="block text-sm font-medium text-text-primary mb-2">{t('auth.password')}</label>
                  <input
                    type="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className="w-full h-11 px-4 bg-bg-control border border-border rounded-lg text-text-primary placeholder-text-secondary outline-none transition-all focus:border-accent focus:bg-bg-control-hover"
                    placeholder="••••••••"
                    required
                  />
                </div>
              )}

              {!passwordMode && (
                <div className="flex items-start gap-3">
                  <input
                    id="accept-terms"
                    type="checkbox"
                    checked={acceptTerms}
                    onChange={(e) => setAcceptTerms(e.target.checked)}
                    className="mt-1 w-4 h-4 rounded border-border bg-bg-control checked:bg-accent"
                    required
                  />
                  <label htmlFor="accept-terms" className="text-xs text-text-secondary cursor-pointer">
                    {t('auth.termsAccept')}{' '}
                    <a
                      href="https://prosecaptain.com/terms-and-conditions"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-accent hover:underline"
                      onClick={(e) => e.stopPropagation()}
                    >
                      {t('auth.terms')}
                    </a>{' '}
                    {t('auth.and')}{' '}
                    <a
                      href="https://prosecaptain.com/privacy-policy"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-accent hover:underline"
                      onClick={(e) => e.stopPropagation()}
                    >
                      {t('auth.privacy')}
                    </a>
                  </label>
                </div>
              )}

              {/* Turnstile container - only on production */}
              {isProductionHost() && (
                <div ref={turnstileContainerRef} className="fixed -left-[9999px] w-[300px] h-[65px] overflow-hidden" aria-hidden="true"></div>
              )}

              <button
                type="submit"
                disabled={loading || (!passwordMode && !acceptTerms) || (isProductionHost() && !turnstileToken)}
                className={`w-full h-11 font-semibold rounded-full transition-all ${
                  (isProductionHost() && !turnstileToken) || (!passwordMode && !acceptTerms)
                    ? 'bg-bg-control text-text-disabled cursor-not-allowed'
                    : 'bg-accent hover:bg-accent-hover text-bg-main'
                }`}
              >
                {loading ? (
                  <span className="inline-flex items-center justify-center gap-2">
                    <span className="w-4 h-4 border-2 border-bg-main border-t-transparent rounded-full animate-spin"></span>
                    <span>{t('auth.sendingCode')}</span>
                  </span>
                ) : (
                  passwordMode ? t('button.signInShort') : t('auth.sendVerificationCode')
                )}
              </button>

              {passwordMode && (
                <button
                  type="button"
                  onClick={() => {
                    setPasswordMode(false);
                    setPassword('');
                    setError('');
                  }}
                  className="w-full h-10 rounded-full border border-border text-text-primary hover:bg-bg-control transition-all"
                >
                  {t('auth.useCodeRegistration')}
                </button>
              )}
            </form>
          ) : (
            <form ref={codeFormRef} onSubmit={verifyRegistrationCode} className="space-y-5">
              <div className="flex items-center justify-between gap-2">
                {code.map((digit, idx) => (
                  <input
                    key={idx}
                    ref={(el) => (codeInputRefs.current[idx] = el)}
                    type="text"
                    inputMode="numeric"
                    maxLength={1}
                    value={digit}
                    onChange={(e) => handleCodeChange(idx, e.target.value)}
                    onKeyDown={(e) => handleCodeKeyDown(idx, e)}
                    className="w-12 h-14 text-center text-xl bg-bg-control border border-border rounded-lg text-text-primary outline-none transition-all focus:border-accent"
                  />
                ))}
              </div>
              <button
                type="submit"
                disabled={loading}
                className="w-full h-11 font-semibold rounded-full transition-all bg-accent hover:bg-accent-hover text-bg-main disabled:bg-bg-control disabled:text-text-disabled"
              >
                {loading ? t('auth.verifying') : t('auth.verifyAndCreateAccount')}
              </button>
              <button
                type="button"
                onClick={resendCode}
                disabled={loading}
                className="w-full h-10 rounded-full border border-border text-text-primary hover:bg-bg-control transition-all"
              >
                {t('auth.resendCode')}
              </button>
            </form>
          )}

          <div className="mt-6 text-center">
            <p className="text-text-secondary text-sm">
              {t('auth.alreadyHaveAccount')}{' '}
              <button
                onClick={() => navigate('/signin')}
                className="text-accent hover:underline font-medium"
              >
                {t('auth.signIn')}
              </button>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}

